import React, { useEffect, useState } from "react";
import api from "@/api";
import { Plus, Edit, Users, Trash2, KeyRound } from "lucide-react";

function UserModal({ user, companies, onClose, onSave }) {
  const isEdit = !!user?.id;
  const [form, setForm] = useState(user ? { ...user, password: "" } : { full_name: "", email: "", password: "", role: "company_user", company_id: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        const update = { full_name: form.full_name, role: form.role, is_active: form.is_active, company_id: form.company_id };
        await api.put(`/admin/users/${user.id}`, update);
      } else {
        await api.post("/admin/users", form);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-md animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200">
          <h3 className="font-bold text-slate-900">{isEdit ? "Edit User" : "New User"}</h3>
        </div>
        <form onSubmit={handleSave} className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Full Name</label>
            <input data-testid="user-fullname-input" type="text" value={form.full_name} onChange={e => setForm(f=>({...f,full_name:e.target.value}))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
          </div>
          {!isEdit && (
            <>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Email</label>
                <input data-testid="user-email-input" type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Password</label>
                <input data-testid="user-password-input" type="password" value={form.password} onChange={e => setForm(f=>({...f,password:e.target.value}))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Role</label>
            <select data-testid="user-role-select" value={form.role} onChange={e=>setForm(f=>({...f,role:e.target.value}))} className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
              <option value="company_user">Company User</option>
              <option value="company_admin">Company Admin</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">Company</label>
            <select data-testid="user-company-select" value={form.company_id} onChange={e=>setForm(f=>({...f,company_id:e.target.value}))} required className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] bg-white">
              <option value="">Select company...</option>
              {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {isEdit && (
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={form.is_active} onChange={e=>setForm(f=>({...f,is_active:e.target.checked}))} className="accent-[#006080]" />
              <label htmlFor="is_active" className="text-sm text-slate-700">Active</label>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">Cancel</button>
            <button data-testid="save-user-button" type="submit" disabled={saving} className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50">
              {saving ? "Saving..." : isEdit ? "Save" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ user, onClose }) {
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: newPassword });
      setSuccess("Password reset successfully.");
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Reset failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 w-full max-w-sm animate-fade-in" onClick={e => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
          <KeyRound size={15} className="text-[#006080]" />
          <h3 className="font-bold text-slate-900">Reset Password</h3>
        </div>
        <form onSubmit={handleReset} className="p-6 space-y-4">
          <p className="text-sm text-slate-500">
            Set a new password for <span className="font-medium text-slate-700">{user.email}</span>.
          </p>
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">{error}</div>}
          {success && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm text-emerald-700">{success}</div>}
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 block mb-1.5">New Password</label>
            <input
              data-testid="reset-password-input"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              placeholder="Min. 8 characters"
              className="w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080]"
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-9 border border-slate-200 text-sm font-medium text-slate-700 rounded-md hover:bg-slate-50">
              Cancel
            </button>
            <button
              data-testid="confirm-reset-password-button"
              type="submit"
              disabled={saving || !!success}
              className="flex-1 h-9 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] disabled:opacity-50"
            >
              {saving ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const fetchAll = () => {
    setLoading(true);
    Promise.all([api.get("/admin/users"), api.get("/admin/companies")]).then(([u, c]) => {
      setUsers(u.data);
      setCompanies(c.data);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchAll(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Deactivate this user?")) return;
    await api.delete(`/admin/users/${id}`);
    fetchAll();
  };

  const ROLE_BADGE = { company_admin: "bg-[#E5F0F2] text-[#006080]", company_user: "bg-slate-100 text-slate-600" };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Users</h2>
          <p className="text-sm text-slate-500">Manage users across all companies.</p>
        </div>
        <button onClick={() => setModal({ type: "create" })} data-testid="create-user-button" className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors">
          <Plus size={14} /> New User
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["User", "Company", "Role", "Status", "Joined", ""].map(h => (
                  <th key={h} className="text-left text-xs font-bold uppercase tracking-wide text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u, i) => {
                const co = companies.find(c => c.id === u.company_id);
                const initials = u.full_name?.split(" ").map(w=>w[0]).join("").toUpperCase().slice(0,2)||"?";
                return (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${i%2===1?"bg-slate-50/50":""}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#E5F0F2] flex items-center justify-center text-[#006080] text-xs font-bold">{initials}</div>
                        <div>
                          <p className="font-medium text-slate-900">{u.full_name}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-xs">{co?.name || "—"}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${ROLE_BADGE[u.role]||"bg-slate-100 text-slate-600"}`}>{u.role?.replace("_"," ")}</span></td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-0.5 rounded-full ${u.is_active?"bg-emerald-50 text-emerald-700":"bg-red-50 text-red-600"}`}>{u.is_active?"Active":"Inactive"}</span></td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{u.created_at?.slice(0,10)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setModal({type:"edit",user:u})} data-testid={`edit-user-${u.id}`} className="p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"><Edit size={13}/></button>
                        <button onClick={() => setModal({type:"reset",user:u})} data-testid={`reset-password-${u.id}`} title="Reset password" className="p-1.5 hover:bg-amber-50 rounded text-slate-400 hover:text-amber-600"><KeyRound size={13}/></button>
                        <button onClick={() => handleDelete(u.id)} data-testid={`delete-user-${u.id}`} className="p-1.5 hover:bg-red-50 rounded text-slate-400 hover:text-red-500"><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="text-center py-12"><Users size={28} className="text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No users yet</p></div>
          )}
        </div>
      )}

      {modal?.type === "create" && <UserModal companies={companies} onClose={()=>setModal(null)} onSave={()=>{setModal(null);fetchAll();}} />}
      {modal?.type === "edit" && <UserModal user={modal.user} companies={companies} onClose={()=>setModal(null)} onSave={()=>{setModal(null);fetchAll();}} />}
      {modal?.type === "reset" && <ResetPasswordModal user={modal.user} onClose={()=>setModal(null)} />}
    </div>
  );
}
